import React, { useMemo, useState } from 'react';
import Card from '../Card';
import SearchableSelect from '../SearchableSelect';
import { Tabs } from '../ui';
import {
  HBarChart, TimeChart, formatCost, formatNumber, formatPercent, PALETTE,
} from '../charts';

// The Overview's Routers section: what each model router's calls cost, which models it
// picked, and its cost over time. Every figure is what the router stated for its calls
// (usage_logs.cost_usd), which is what the users' budgets were charged, not a price we
// worked out. Data: GET /api/dashboard/routers, under the page's filters.
//
// Hidden by the page when no router is defined and none served a call in the window.

const MODEL_METRICS = [
  { value: 'calls', label: 'Calls' },
  { value: 'cost', label: 'Cost' },
];

const plural = (n, word) => `${formatNumber(n)} ${word}${n === 1 ? '' : 's'}`;

export default function RouterInsights({ data }) {
  // '' is every router. A router picked here that has no calls under a later filter falls
  // back to every router rather than an empty chart the selector no longer offers.
  const [pick, setPick] = useState('');
  const [metric, setMetric] = useState('calls');
  const routers = useMemo(() => data?.routers || [], [data]);
  const totals = data?.totals || { calls: 0, cost_usd: 0 };
  const picked = routers.find(r => r.router_key === pick) || null;

  // One colour per router, in the order the cost chart lists them, so a router's bar and
  // its line over time are the same colour.
  const routerColor = useMemo(() => {
    const out = {};
    routers.forEach((r, i) => { out[r.router_key] = PALETTE[i % PALETTE.length]; });
    return out;
  }, [routers]);

  // One colour per model across every router, so a model keeps its colour when the chart
  // switches router. Ordered by total calls, so the most used models get the first hues.
  const modelColor = useMemo(() => {
    const calls = {};
    routers.forEach(r => (r.models || []).forEach((m) => {
      calls[m.model] = (calls[m.model] || 0) + (m.calls || 0);
    }));
    const out = {};
    Object.keys(calls).sort((a, b) => calls[b] - calls[a] || a.localeCompare(b))
      .forEach((m, i) => { out[m] = PALETTE[(i + 1) % PALETTE.length]; });
    return out;
  }, [routers]);

  const costBars = routers.map(r => ({
    key: r.router_key, name: r.name, count: r.cost_usd, calls: r.calls, synthetic: r.deleted,
  }));

  const models = useMemo(() => {
    const byModel = {};
    (picked ? [picked] : routers).forEach(r => (r.models || []).forEach((m) => {
      const row = byModel[m.model] || (byModel[m.model] = { name: m.model, calls: 0, cost: 0 });
      row.calls += m.calls || 0;
      row.cost += m.cost_usd || 0;
    }));
    const rows = Object.values(byModel).map(r => ({ ...r, count: metric === 'cost' ? r.cost : r.calls }));
    const sum = rows.reduce((s, r) => s + r.count, 0);
    return rows
      .map(r => ({ ...r, share: sum > 0 ? r.count / sum : 0 }))
      .filter(r => r.count > 0)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [routers, picked, metric]);

  const series = data?.over_time?.series || [];
  const overTime = data?.over_time?.data || [];
  const perCall = totals.calls > 0 ? totals.cost_usd / totals.calls : 0;
  const scope = picked ? picked.name : 'every router';
  const empty = totals.calls === 0 ? 'No router calls in this window' : null;

  return (
    <>
      <div className="section-head">
        <h2>Routers</h2>
        <p>what they cost, and the models they chose</p>
      </div>
      <div className="dashboard-grid">
        <Card title="Cost by router"
              subtitle={totals.calls > 0
                ? `${formatCost(totals.cost_usd)} · ${plural(totals.calls, 'call')} · ${formatCost(perCall)} a call`
                : 'the cost each router stated for its calls'}
              help="What each router's calls cost in this window: the cost the router stated for every call, which is what the users' budgets were charged. Days older than the Activity logs window come from the daily totals, except when filtering by user, tier or source."
              span={6}
              empty={empty}>
          <HBarChart
            data={costBars}
            nameWidth={110}
            valueWidth={132}
            formatVal={(v, row) => `${formatCost(v)} · ${plural(row.calls, 'call')}`}
            colorFn={(row) => (row.synthetic ? 'var(--ink-4)' : routerColor[row.key])}
          />
        </Card>
        <Card title="Models chosen"
              subtitle={`${metric === 'cost' ? 'cost' : 'calls'} of ${scope}, by the model that answered`}
              help="Which model the router picked for each call. The router names it in every answer, and the platform records it. The model filter above applies to it."
              span={6}
              empty={empty}>
          <div className="tile-toolbar">
            {routers.length > 1 && (
              <SearchableSelect
                value={picked ? pick : ''}
                onChange={setPick}
                placeholder="All routers"
                searchPlaceholder="Search routers…"
                aria-label="Router shown on the models chart"
                options={routers.map(r => ({ value: r.router_key, label: r.name }))}
                minWidth={150}
              />
            )}
            <Tabs size="sm" label="Count models by" value={metric} onChange={setMetric}
                  tabs={MODEL_METRICS} />
          </div>
          <HBarChart
            data={models}
            nameWidth={140}
            valueWidth={104}
            formatVal={(v, row) => `${formatPercent(row.share * 100, 0)} · ${
              metric === 'cost' ? formatCost(v) : formatNumber(v)}`}
            colorFn={(row) => modelColor[row.name]}
            emptyText="No calls for this router in this window"
          />
        </Card>
        <Card title="Router cost over time"
              subtitle="per router"
              span={12}
              empty={empty}>
          <TimeChart
            data={overTime}
            labelKey="date"
            lines={series.map(s => ({ key: s.key, label: s.label }))}
            colors={series.map(s => routerColor[s.key] || PALETTE[0])}
            height={220}
            legend
            formatY={formatCost}
            formatValue={formatCost}
            scaleFloor={0}
          />
        </Card>
      </div>
    </>
  );
}
